import { defineStore } from "pinia";
import api from "@/services/api"; // ✅ your axios instance

function pickLang(obj, lang = "ar") {
  if (!obj || typeof obj !== "object") return "";
  return obj[lang] ?? obj.ar ?? obj.en ?? "";
}

const toKey = (id) => String(id ?? ""); // ✅ unify keys (string)

export const useConsultationServicesStore = defineStore(
  "consultationServices",
  {
    state: () => ({
      items: [],
      byId: {},

      loadingList: false,
      loadingOne: false,

      error: null,
      lastFetchedAt: 0, // simple cache TTL
    }),

    getters: {
      hasItems: (s) => Array.isArray(s.items) && s.items.length > 0,

      // ✅ FIX: now exists
      getById: (s) => (id) => {
        const key = toKey(id);
        return s.byId?.[key] ?? null;
      },
    },

    actions: {
      async fetchAll({ force = false, ttlMs = 60_000 } = {}) {
        const now = Date.now();

        // ✅ cache
        if (!force && this.hasItems && now - this.lastFetchedAt < ttlMs) return;

        this.loadingList = true;
        this.error = null;

        try {
          const res = await api.get("/consultation-services");
          const list = res?.data?.data ?? [];

          this.items = list;

          // ✅ key as string to avoid "1" vs 1 issues
          this.byId = list.reduce((acc, it) => {
            acc[toKey(it.id)] = it;
            return acc;
          }, {});

          this.lastFetchedAt = now;
        } catch (e) {
          this.error =
            e?.response?.data?.message ||
            e?.message ||
            "Failed to load consultation services.";
        } finally {
          this.loadingList = false;
        }
      },

      async fetchById(id, { force = false } = {}) {
        if (id == null) return null;

        const key = toKey(id);

        // ✅ cache first
        if (!force && this.byId[key]) return this.byId[key];

        this.loadingOne = true;
        this.error = null;

        try {
          // If no show endpoint -> fallback to list
          await this.fetchAll({ force: true, ttlMs: 0 });
          return this.byId[key] ?? null;
        } catch (e) {
          this.error =
            e?.response?.data?.message ||
            e?.message ||
            "Failed to load service.";
          return null;
        } finally {
          this.loadingOne = false;
        }
      },

      getLocalizedTitle(item, lang) {
        return pickLang(item?.name, lang);
      },

      getLocalizedDesc(item, lang) {
        return pickLang(item?.description, lang);
      },

      getLocalizedAreasHtml(item, lang) {
        return pickLang(item?.support_areas, lang);
      },
    },
  },
);
